// ChargemasterImportedPreview — a spreadsheet-styled preview of a hospital's
// price file built from the rows we imported from it (the per-hospital static
// JSON every profile page already loads), so the Chargemaster Archive tab
// always shows the file's contents even when the original object cannot be
// fetched (retired bucket pointers, missing public URL, multi-GB files).
//
// Why (Trent, 2026-09-21): the archive tab used to fetch the raw file from R2
// and parse it in the browser; a missing or oversized file left a red
// "Failed to load file" and nothing else. These are the same lines, from the
// same file, already parsed — so the reader gets a real preview first, and
// the raw file is an optional upgrade on top.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { StaticMapping } from '@/hooks/useStaticHospitalData';

export interface ImportedPreviewProps {
  mappings: StaticMapping[];
  hospitalName: string;
  fileName?: string | null;
  vintageYear?: number | string | null;
  /** Raw (pre-dedup) row count from mappings_meta, when known. */
  totalRaw?: number | null;
  /** Optional top-right slot (e.g. the "View original file" button). */
  actions?: React.ReactNode;
  /** A one-line note above the sheet (e.g. why the raw file isn't shown). */
  notice?: string | null;
  /** Rows to show per page. */
  pageSize?: number;
  /** Set when only the first N rows (by charge) were pulled from the database. */
  partial?: number | null;
  isDark?: boolean;
}

interface Col { key: string; label: string; align: 'left' | 'right'; get: (m: StaticMapping) => string; width?: number }

const fmtUSD = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? '' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const str = (v: unknown) => (v == null ? '' : String(v));

/** Excel-style column letters: A…Z, AA…  */
const colLetter = (i: number) => { let s = ''; let n = i; do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0); return s; };

export function ChargemasterImportedPreview({ mappings, hospitalName, fileName, vintageYear, totalRaw, actions, notice, pageSize = 50, isDark = false, partial = null }: ImportedPreviewProps) {
  const { t } = useTranslation(['hospital']);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) => t(`hospital:archivePreview.${key}`, { defaultValue: fallback, ...(opts || {}) });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  // Only columns this file actually fills — a CSV with no NDC column shouldn't
  // show an empty NDC column. Order mirrors the CMS template.
  const columns = useMemo<Col[]>(() => {
    const has = (f: (m: StaticMapping) => unknown) => mappings.some((m) => { const v = f(m); return v != null && v !== ''; });
    const all: Col[] = [
      { key: 'pd', label: tr('cols.description', 'description'), align: 'left', get: (m) => str(m.pd), width: 340 },
      { key: 'cc', label: tr('cols.cdm', 'local code'), align: 'left', get: (m) => str(m.cc), width: 110 },
      { key: 'cp', label: 'CPT', align: 'left', get: (m) => str(m.cp), width: 80 },
      { key: 'hc', label: 'HCPCS', align: 'left', get: (m) => str(m.hc), width: 80 },
      { key: 'dr', label: 'MS-DRG', align: 'left', get: (m) => str(m.dr), width: 80 },
      { key: 'rv', label: tr('cols.rev', 'revenue code'), align: 'left', get: (m) => str(m.rv), width: 90 },
      { key: 'nd', label: 'NDC', align: 'left', get: (m) => str(m.nd), width: 120 },
      { key: 'io', label: tr('cols.setting', 'setting'), align: 'left', get: (m) => str(m.io), width: 100 },
      { key: 'nu', label: tr('cols.unit', 'unit'), align: 'left', get: (m) => str(m.nu), width: 80 },
      { key: 'gc', label: tr('cols.gross', 'standard_charge | gross'), align: 'right', get: (m) => fmtUSD(m.gc), width: 150 },
      { key: 'dc', label: tr('cols.cash', 'standard_charge | discounted_cash'), align: 'right', get: (m) => fmtUSD(m.dc), width: 190 },
      { key: 'mn', label: tr('cols.min', 'standard_charge | min'), align: 'right', get: (m) => fmtUSD(m.mn), width: 140 },
      { key: 'mx', label: tr('cols.max', 'standard_charge | max'), align: 'right', get: (m) => fmtUSD(m.mx), width: 140 },
    ];
    return all.filter((c) => (c.key === 'pd' || c.key === 'gc') ? true : has((m) => (m as any)[c.key]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return mappings;
    return mappings.filter((m) => [m.pd, m.cc, m.cp, m.hc, m.dr, m.rv, m.nd].some((v) => v && String(v).toLowerCase().includes(s)));
  }, [mappings, q]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pages);
  const rows = filtered.slice((cur - 1) * pageSize, cur * pageSize);

  const P = isDark
    ? { sheetBg: '#0b1630', gridLine: 'rgba(255,255,255,0.10)', headBg: '#122246', headInk: 'rgba(240,235,227,0.85)', gutterBg: '#10203f', gutterInk: 'rgba(240,235,227,0.55)', cellInk: '#F0EBE3', zebra: 'rgba(255,255,255,0.025)', letterBg: '#0e1c3a' }
    : { sheetBg: '#FFFFFF', gridLine: '#D9DEE7', headBg: '#EEF2F8', headInk: '#1F2A44', gutterBg: '#F3F5F9', gutterInk: '#6B7280', cellInk: '#111827', zebra: '#FAFBFD', letterBg: '#E7ECF4' };
  const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

  return (
    <div className="flex flex-col h-full min-h-0" style={{ color: P.cellInk }}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate" title={fileName || undefined}>{fileName || tr('untitled', "{{name}}'s published price file", { name: hospitalName })}</div>
          <div className="text-xs text-muted-foreground">
            {partial && mappings.length >= partial
              ? tr('stampPartial', 'Preview of the {{n}} highest-charge lines we imported from this file{{year}}; the full list is on the Services tab.', { n: mappings.length.toLocaleString(), year: vintageYear ? tr('stampYear', ' · {{year}} file', { year: vintageYear }) : '' })
              : tr('stamp', 'Preview built from the {{n}} priced lines we imported from this file{{raw}}{{year}}.', {
              n: mappings.length.toLocaleString(),
              raw: totalRaw && totalRaw > mappings.length ? tr('stampRaw', ' ({{raw}} rows before payer de-duplication)', { raw: totalRaw.toLocaleString() }) : '',
              year: vintageYear ? tr('stampYear', ' · {{year}} file', { year: vintageYear }) : '',
            })}
          </div>
        </div>
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={tr('search', 'Find a code or description…')} className="pl-9 h-9" aria-label={tr('search', 'Find a code or description…')} />
        </div>
        {actions}
      </div>
      {notice && <div className="mb-2 text-xs rounded-md px-3 py-2 border" style={{ borderColor: P.gridLine, background: P.zebra }}>{notice}</div>}

      {/* The sheet */}
      <div className="flex-1 min-h-0 overflow-auto rounded-md border" style={{ borderColor: P.gridLine, background: P.sheetBg }}>
        <table className="border-collapse text-[12.5px]" style={{ fontFamily: mono, minWidth: '100%' }}>
          <thead className="sticky top-0 z-10">
            <tr style={{ background: P.letterBg, color: P.gutterInk, fontSize: 10.5 }}>
              <th className="sticky left-0 z-20 font-normal text-center" style={{ background: P.letterBg, width: 56, minWidth: 56, borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '3px 4px' }} />
              {columns.map((c, i) => (
                <th key={c.key} className="font-normal text-center" style={{ borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '3px 6px', minWidth: c.width }}>{colLetter(i)}</th>
              ))}
            </tr>
            <tr style={{ background: P.headBg, color: P.headInk }}>
              <th className="sticky left-0 z-20 text-center font-semibold" style={{ background: P.headBg, borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '6px 4px', color: P.gutterInk }}>1</th>
              {columns.map((c) => (
                <th key={c.key} className="font-semibold whitespace-nowrap" style={{ textAlign: c.align, borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '6px 8px' }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => {
              const rowNo = (cur - 1) * pageSize + i + 2; // row 1 is the header
              return (
                <tr key={`${rowNo}-${m.cc || m.cp || m.hc || ''}`} style={{ background: i % 2 ? P.zebra : 'transparent' }}>
                  <td className="sticky left-0 z-10 text-center" style={{ background: P.gutterBg, color: P.gutterInk, borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '4px 4px', fontSize: 11 }}>{rowNo}</td>
                  {columns.map((c) => {
                    const v = c.get(m);
                    return (
                      <td key={c.key} className={c.key === 'pd' ? '' : 'whitespace-nowrap'} title={v} style={{ textAlign: c.align, borderRight: `1px solid ${P.gridLine}`, borderBottom: `1px solid ${P.gridLine}`, padding: '4px 8px', maxWidth: c.key === 'pd' ? 480 : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: c.key === 'pd' ? 'nowrap' : undefined }}>{v}</td>
                    );
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="text-center text-muted-foreground" style={{ padding: 24 }}>{tr('noMatch', 'No line matches that search.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
        <div>
          {tr('showing', 'Rows {{from}}–{{to}} of {{total}}', { from: filtered.length ? ((cur - 1) * pageSize + 1).toLocaleString() : 0, to: Math.min(cur * pageSize, filtered.length).toLocaleString(), total: filtered.length.toLocaleString() })}
          {filtered.length !== mappings.length && ` · ${tr('filteredFrom', 'filtered from {{n}}', { n: mappings.length.toLocaleString() })}`}
          {' · '}{columns.length} {tr('columns', 'columns')}
        </div>
        {pages > 1 && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, cur - 1))} disabled={cur === 1}><ChevronLeft className="w-4 h-4" /></Button>
            <span>{tr('page', 'Page {{p}} of {{n}}', { p: cur, n: pages })}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.min(pages, cur + 1))} disabled={cur === pages}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
